import { FC, useCallback, useMemo } from 'react'

import InputItem from '../../../../../components/InputItem'
import { sortDropdown } from '../../../../../shared/formFunctions'
import { IMaterialEmissionConfiguration } from '../types'

interface IMaterialInfo {
  formik: any
  configuration: IMaterialEmissionConfiguration
  code: string | null
  onCategoryChange(e: React.ChangeEvent<Element>): void
}

// Virgin/Recycled is an explicit choice, never inferred from the material name containing
// "recycled": virgin and recycled are separate records and this is what tells them apart.
const RECYCLED_OPTIONS = [
  { id: 0, name: 'Virgin EF' },
  { id: 1, name: 'Recycled EF' },
]

export const MaterialInfo: FC<IMaterialInfo> = ({
  formik,
  configuration,
  code,
  onCategoryChange,
}) => {
  // The select hands its value back as a string, so a selected "Virgin EF" arrives as "0" — truthy,
  // which flipped the reading to Recycled and hoisted the wrong option to the top of the list until
  // a save normalised the value to a boolean (bug 39326). Compare numerically, not for truthiness.
  const recycledValue = useMemo(() => {
    const raw = formik.values.isRecycled

    if (raw === null || raw === undefined || raw === '') {
      return -1
    }
    return Number(raw) ? 1 : 0
  }, [formik.values.isRecycled])

  // Configuration on demand: Material name is filtered to the selected Category rather than
  // fetched per level, the same cascade the packaging processing activity form uses.
  const functionalUnitOptions = useMemo(
    () =>
      configuration.packagingMaterialsTaxonomies.functionalUnits.filter(
        fu => fu.categoryId === Number(formik.values.packagingMaterialCategoryId)
      ),
    [
      configuration.packagingMaterialsTaxonomies.functionalUnits,
      formik.values.packagingMaterialCategoryId,
    ]
  )

  // A plain on/off flag: the toggle writes the boolean the wire contract expects, with no
  // "unset" third state to fall back to.
  const handleIncludesProcessingImpactsChange = useCallback(
    (checked: boolean) => formik.setFieldValue('includesProcessingImpacts', checked),
    [formik]
  )

  return (
    <div className="piece-content-inputs mb-5">
      <InputItem
        disabled
        name="Class"
        description="Packaging material emission factors all sit under one class."
        type="input"
        onChange={formik.handleChange}
        formikName="packagingMaterialClass"
        formik={formik}
        defaultValue="Packaging Material"
      />
      <InputItem
        disabled
        name="Product type"
        description="Fixed for packaging material emission factors and submitted with the record."
        type="input"
        onChange={formik.handleChange}
        formikName="productTypeDisplay"
        formik={formik}
        defaultValue="Packaging Material"
      />
      <InputItem
        name="Category"
        description="The packaging material category this emission factor describes."
        type="select"
        onChange={onCategoryChange}
        formikName="packagingMaterialCategoryId"
        showAsterisk
        formik={formik}
        defaultValue={formik.values.packagingMaterialCategoryId ?? -1}
        options={sortDropdown(
          configuration.packagingMaterialsTaxonomies.categories,
          formik.values.packagingMaterialCategoryId ?? -1,
          'name'
        )}
      />
      <InputItem
        disabled={!formik.values.packagingMaterialCategoryId}
        name="Material name"
        description="Select a category first; this list is filtered to that category."
        type="select"
        onChange={formik.handleChange}
        formikName="productId"
        showAsterisk
        formik={formik}
        defaultValue={formik.values.productId ?? -1}
        options={sortDropdown(functionalUnitOptions, formik.values.productId ?? -1, 'name')}
      />
      <InputItem
        // Deterministic reset key: remount only when the selected country actually changes, so
        // the creatable select picks up an externally set value. The food page this was copied
        // from adds Math.random() here, which remounts on EVERY render and can drop focus and
        // in-progress input.
        key={formik.values.countryId ?? -1}
        name="Country Name"
        description="The country for which this entry was generated."
        type="select"
        onChange={formik.handleChange}
        formikName="countryId"
        create={true}
        formik={formik}
        defaultValue={formik.values.countryId ?? -1}
        options={sortDropdown(
          configuration.countries,
          formik.values.countryId ?? -1,
          'countryName'
        )}
      />
      <InputItem
        name="Country ISO Code"
        description="Please select the country in which the data was collected."
        type="select"
        create={true}
        onChange={formik.handleChange}
        formikName="countryId"
        formik={formik}
        defaultValue={formik.values.countryId ?? -1}
        options={sortDropdown(configuration.countries, formik.values.countryId ?? -1, 'countryISO')}
      />
      <InputItem
        name="Region"
        description="The region this emission factor applies to."
        type="select"
        onChange={formik.handleChange}
        formikName="regionIdentifierId"
        formik={formik}
        defaultValue={formik.values.regionIdentifierId ?? -1}
        options={sortDropdown(
          configuration.regions,
          formik.values.regionIdentifierId ?? -1,
          'name'
        )}
      />
      <InputItem
        name="Virgin / Recycled"
        description="One record is one material, one country and one of these two states."
        type="select"
        onChange={formik.handleChange}
        formikName="isRecycled"
        showAsterisk
        formik={formik}
        defaultValue={recycledValue}
        options={sortDropdown(RECYCLED_OPTIONS, recycledValue, 'name')}
      />
      <InputItem
        name="Includes processing impacts"
        description="Toggle on if this material's Ecoinvent EF already includes the manufacturing or forming step."
        type="toggle"
        onToggleChange={handleIncludesProcessingImpactsChange}
        formikName="includesProcessingImpacts"
        formik={formik}
        defaultValue={Boolean(formik.values.includesProcessingImpacts)}
      />
      <InputItem
        name="Functional unit"
        description="The unit this emission factor is expressed per. Carries through to any processing activity using this material."
        type="select"
        onChange={formik.handleChange}
        formikName="functionalUnit"
        showAsterisk
        formik={formik}
        defaultValue={formik.values.functionalUnit ?? -1}
        options={sortDropdown(
          configuration.functionalUnits,
          formik.values.functionalUnit ?? -1,
          'description'
        )}
      />
      <InputItem
        disabled
        name="External code"
        description="Generated on save."
        type="input"
        onChange={formik.handleChange}
        formikName="code"
        formik={formik}
        defaultValue={code ?? ''}
      />
    </div>
  )
}
